/**
 * Zoho SalesIQ Chat Widget Integration
 * 
 * This component integrates Zoho SalesIQ chat for the Student Portal.
 * When a logged-in student uses the chat, SalesIQ identifies them via email
 * and routes the chat to their assigned counselor (Lead Owner / Contact Owner in Zoho CRM).
 * 
 * Configuration required in Zoho SalesIQ:
 * 1. Connect SalesIQ to Zoho CRM (Settings > Integrations > Zoho CRM)
 * 2. Create Visitor Routing rule:
 *    - Audience: "Zoho CRM Contact" OR "Zoho CRM Lead"
 *    - Route to: Dynamic Selection → CRM Lead/Contact Owner
 *    - Fallback: Any available operator
 * 
 * Props:
 * - hideFloatingButton: Set to true to hide the default floating chat icon
 *                       (useful when using custom "Chat with Counselor" button)
 */

import { useEffect } from 'react';
import { useStudentAuth } from '../context/StudentAuthContext';

// SalesIQ Widget ID - Set this in environment or configure here
const SALESIQ_WIDGET_CODE = process.env.REACT_APP_ZOHO_SALESIQ_WIDGET_CODE;

// Set to true to hide the floating chat button (users will use "Chat with Counselor" button instead)
const HIDE_FLOATING_BUTTON = false; // Change to true if you want to hide the bottom-right icon

const ZohoSalesIQ = () => {
  const { user, isAuthenticated } = useStudentAuth();

  useEffect(() => {
    // Only load SalesIQ for authenticated portal users
    if (!isAuthenticated) {
      // Hide widget if it exists and user is not authenticated
      if (window.$zoho && window.$zoho.salesiq) {
        if (window.$zoho.salesiq.floatbutton) {
          window.$zoho.salesiq.floatbutton.visible('hide');
        }
        if (window.$zoho.salesiq.floatwindow) {
          window.$zoho.salesiq.floatwindow.visible('hide');
        }
      }
      // Also try to remove the widget container from DOM
      const widgetContainer = document.querySelector('.zsiq_floatmain');
      if (widgetContainer) {
        widgetContainer.style.display = 'none';
      }
      return;
    }
    
    // Don't load if widget code not configured
    if (!SALESIQ_WIDGET_CODE) {
      console.warn('[ZohoSalesIQ] Widget code not configured. Set REACT_APP_ZOHO_SALESIQ_WIDGET_CODE');
      return;
    }

    // Show widget container if hidden
    const widgetContainer = document.querySelector('.zsiq_floatmain');
    if (widgetContainer) {
      widgetContainer.style.display = '';
    }

    // If SalesIQ is already loaded, just update visitor info and show
    if (window.$zoho && window.$zoho.salesiq) {
      if (window.$zoho.salesiq.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('show');
      }
      updateVisitorInfo();
      return;
    }

    // Load SalesIQ script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'zsiqscript';
    script.defer = true;
    script.src = `https://salesiq.zohopublic.com/widget?wc=${SALESIQ_WIDGET_CODE}`;
    
    // Initialize SalesIQ settings before script loads
    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || {
      widgetcode: SALESIQ_WIDGET_CODE,
      values: {},
      ready: function() {
        updateVisitorInfo();
      }
    };

    document.body.appendChild(script);

    return () => {
      // Hide widget on unmount if user logs out
      if (window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('hide');
      }
    };
  }, [isAuthenticated]);

  // Update visitor info when user changes
  useEffect(() => {
    if (window.$zoho && window.$zoho.salesiq) {
      if (isAuthenticated) {
        updateVisitorInfo();
        if (window.$zoho.salesiq.floatbutton) {
          window.$zoho.salesiq.floatbutton.visible('show');
        }
      } else {
        if (window.$zoho.salesiq.floatbutton) {
          window.$zoho.salesiq.floatbutton.visible('hide');
        }
      }
    }
  }, [user, isAuthenticated]);

  const updateVisitorInfo = () => {
    if (!window.$zoho || !window.$zoho.salesiq) return;

    const salesiq = window.$zoho.salesiq;

    if (isAuthenticated && user) {
      // Set visitor information for logged-in students
      // This enables SalesIQ to match them to CRM Lead/Contact
      
      if (salesiq.visitor) {
        // Set email - CRITICAL for CRM matching
        if (user.email) {
          salesiq.visitor.email(user.email);
        }
        
        // Set name
        if (user.name) {
          salesiq.visitor.name(user.name);
        }
        
        // Set phone if available
        if (user.phone) {
          salesiq.visitor.contactnumber(user.phone);
        }

        // Set custom visitor info for context
        if (salesiq.visitor.info) {
          salesiq.visitor.info({
            'Student ID': user.user_id || '',
            'Portal User': 'Yes',
            'Registration Date': user.created_at || '',
            'Target Degree': user.target_degree || '',
            'Preferred Countries': (user.preferred_countries || []).join(', '),
          });
        }
      }

      // Show or hide the floating chat button based on config
      // Add a small delay to ensure the widget UI is fully rendered
      setTimeout(() => {
        if (salesiq.floatbutton && salesiq.floatbutton.visible) {
          if (HIDE_FLOATING_BUTTON) {
            salesiq.floatbutton.visible('hide');
          } else {
            salesiq.floatbutton.visible('show');
          }
        }
      }, 500);
      
      // Also ensure float window is available (but don't auto-open)
      // This ensures the chat can be opened programmatically

      console.log('[ZohoSalesIQ] Visitor identified:', user.email);
    } else {
      // For non-authenticated users (login page), HIDE the widget
      if (salesiq.floatbutton && salesiq.floatbutton.visible) {
        salesiq.floatbutton.visible('hide');
      }
      // Also close any open chat window
      if (salesiq.floatwindow && salesiq.floatwindow.visible) {
        salesiq.floatwindow.visible('hide');
      }
      // Try to end/close any chat
      if (salesiq.chat && salesiq.chat.end) {
        salesiq.chat.end();
      }
    }
  };

  // This component doesn't render anything visible
  // The SalesIQ widget handles its own UI
  return null;
};

export default ZohoSalesIQ;
